import CalendarComponent from "@components/Calendar";
import Error from "@components/Error";
import { useCallback, useEffect } from "react";
import useCalendarStore, { ALLOWED_MONTHS } from "@state/CalendarStore";
import useMessageStore from "@state/MessageStore";
import Alert from "@components/Alert";
import { useNavigate } from "react-router-dom";
import useDataStore from "@state/DataStore";
import useLoading from "src/services/LoadingService";
import useBoats from "./use-boats-hook";
import useReservations from "./use-reservations-hook";
import useSettings from "./use-settings-hook";

const Calendar = () => {
  const selectedMonth = useCalendarStore((store) => store.selectedMonth);
  const messages = useMessageStore((state) => state.messages);
  const navigate = useNavigate();
  const activeSeason = useDataStore(
    (state) => state.configuration?.activeSeason
  );

  const {
    fetchReservations,
    loading: reservationsLoading,
    error: reservetionsError,
    reservations,
  } = useReservations(selectedMonth, activeSeason);

  const {
    fetchSettings,
    loading: settingsLoading,
    error: settingsError,
    settings,
  } = useSettings(selectedMonth, activeSeason);

  const {
    fetchBoats,
    loading: boatsLoading,
    error: boatsError,
    boats,
  } = useBoats();

  useEffect(() => {
    if (boats.length === 0) {
      fetchBoats();
    }
  }, [boats.length, fetchBoats]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onReservationClick = useCallback(
    (date: Date, boatId: number) => {
      navigate(`/add/${boatId}/${date.toISOString()}`);
    },
    [navigate]
  );

  const onSettingsClick = useCallback(
    (date: Date, boatId: number) => {
      navigate(`/settings/${boatId}/${date.toISOString()}`);
    },
    [navigate]
  );

  const onMonthChange = useCallback((month: number) => {
    useCalendarStore.getState().setSelectedMonth(month);
  }, []);

  const onDetailClick = useCallback(
    (id: number | string) => {
      navigate(`/detail/${id}`);
    },
    [navigate]
  );

  useLoading(reservationsLoading || boatsLoading || settingsLoading);

  if (settingsError)
    return <Error onAgain={fetchSettings}>{settingsError}</Error>;
  if (boatsError) return <Error onAgain={fetchBoats}>{boatsError}</Error>;
  if (reservetionsError)
    return <Error onAgain={fetchReservations}>{reservetionsError}</Error>;

  return (
    <CalendarComponent
      boats={boats}
      reservations={reservations}
      settings={settings}
      allowedMonths={ALLOWED_MONTHS}
      onMonthChange={onMonthChange}
      selectedMonth={selectedMonth}
      onReservationClick={onReservationClick}
      onSettingsClick={onSettingsClick}
      onDetailClick={onDetailClick}
      currentYear={activeSeason}
      header={
        <>
          {messages.map((message, index) => (
            <Alert
              onClose={() => {
                useMessageStore.getState().removeMessage(index);
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              {message}
            </Alert>
          ))}
        </>
      }
    />
  );
};

export default Calendar;
